import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import {
  listCategoriesAdmin,
  createCategory,
  updateCategoryAdmin,
  deleteCategoryAdmin,
} from "../api";
import { PageHeader } from "./Dashboard";
import RefreshButton from "../components/RefreshButton";

const EMPTY = { slug: "", name: "", display_name: "", tagline: "", order: 0, active: true };

export default function Categories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = () => {
    setLoading(true);
    listCategoriesAdmin().then(setItems).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const save = async (form) => {
    try {
      // Ensure any lingering leading/trailing hyphens are stripped on submit
      const cleaned = { ...form, slug: (form.slug || "").replace(/^-|-$/g, "") };
      if (cleaned._isEdit) await updateCategoryAdmin(cleaned._origSlug, cleaned);
      else await createCategory(cleaned);
      setEditing(null);
      reload();
      toast.success("Category saved");
    } catch (err) {
      // Pydantic 422 returns detail as an ARRAY of { msg, loc, ... }
      const d = err?.response?.data?.detail;
      let msg = "Could not save";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d)) msg = d.map((e) => e?.msg).filter(Boolean).join(" · ") || msg;
      toast.error(msg);
    }
  };

  const del = async (slug) => {
    if (!window.confirm("Delete this category? Products using it must be reassigned first.")) return;
    try {
      await deleteCategoryAdmin(slug);
      reload();
      toast.success("Category removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div data-testid="admin-categories">
      <PageHeader eyebrow="Catalogue" title="Categories" sub="Edit the filters that appear on the public shop and navbar." />
      <div className="p-10">
        <div className="flex items-center justify-between mb-6">
          <RefreshButton onClick={reload} loading={loading} testid="admin-categories-refresh" />
          <button
            onClick={() => setEditing({ ...EMPTY })}
            className="bg-[#c3ba9e] text-[#0d0d0e] px-5 py-3 text-[11px] tracking-[0.28em] uppercase flex items-center gap-2 hover:bg-[#FDFAF6]"
            data-testid="admin-add-category"
          >
            <Plus size={14} /> Add category
          </button>
        </div>
        <div className="bg-[#161617] border border-[#2a2a2d]">
          <div className="grid grid-cols-[1fr_1fr_1.5fr_0.5fr_0.6fr_0.6fr] gap-4 px-6 py-3 border-b border-[#2a2a2d] text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">
            <span>Slug</span><span>Name</span><span>Tagline</span><span>Order</span><span>Active</span><span></span>
          </div>
          {loading ? (
            <p className="p-8 text-center text-[#8c8a7e] text-sm">Loading…</p>
          ) : items.length === 0 ? (
            <p className="p-8 text-center text-[#8c8a7e] text-sm">No categories yet.</p>
          ) : (
            items.map((c) => (
              <div key={c.slug} className="grid grid-cols-[1fr_1fr_1.5fr_0.5fr_0.6fr_0.6fr] gap-4 px-6 py-4 border-b border-[#2a2a2d] items-center" data-testid={`category-${c.slug}`}>
                <span className="text-[#e9e6db] text-sm">{c.slug}</span>
                <span className="text-[#e9e6db] text-sm">{c.display_name || c.name}</span>
                <span className="text-[#9a9787] text-[12px] truncate">{c.tagline}</span>
                <span className="text-[#9a9787] text-sm">{c.order}</span>
                <span className={`text-[10px] tracking-[0.22em] uppercase ${c.active ? "text-[#8ccf9a]" : "text-[#7a7a7a]"}`}>
                  {c.active ? "Live" : "Hidden"}
                </span>
                <div className="flex items-center gap-3 justify-end">
                  <button onClick={() => setEditing({ ...c, _isEdit: true, _origSlug: c.slug })} className="text-[#c3ba9e] hover:text-[#FDFAF6]"><Pencil size={14} /></button>
                  <button onClick={() => del(c.slug)} className="text-[#c97070] hover:text-[#e69a9a]"><Trash2 size={14} /></button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {editing && <CategoryForm initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function CategoryForm({ initial, onCancel, onSave }) {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 z-[60] bg-[#0d0d0e]/70 flex items-center justify-center px-4 py-10" onClick={onCancel}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); onSave(f); }}
        className="bg-[#161617] border border-[#2a2a2d] max-w-xl w-full"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2a2d]">
          <h3 className="font-serif-display text-2xl text-[#e9e6db]">{f._isEdit ? "Edit category" : "Add a category"}</h3>
          <button type="button" onClick={onCancel} className="text-[#9a9787]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 text-[#e9e6db]">
          <Field label="Slug (URL — auto-formatted; use letters, numbers, hyphens)">
            <input
              required
              disabled={f._isEdit}
              value={f.slug}
              onChange={(e) =>
                set(
                  "slug",
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-\s]/g, "")
                    .replace(/\s+/g, "-")
                    .replace(/-+/g, "-")
                )
              }
              onBlur={(e) => set("slug", e.target.value.replace(/^-|-$/g, ""))}
              placeholder="e.g. long-kurtas"
              className={input}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name (admin)"><input required value={f.name} onChange={(e) => set("name", e.target.value)} className={input} /></Field>
            <Field label="Display name (public)"><input value={f.display_name} onChange={(e) => set("display_name", e.target.value)} className={input} /></Field>
          </div>
          <Field label="Tagline"><input value={f.tagline} onChange={(e) => set("tagline", e.target.value)} className={input} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Sort order"><input type="number" value={f.order} onChange={(e) => set("order", Number(e.target.value))} className={input} /></Field>
            <label className="flex items-center gap-2 text-sm cursor-pointer mt-7">
              <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#c3ba9e]" /> Live on storefront
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 pb-6">
          <button type="button" onClick={onCancel} className="px-5 py-3 text-[11px] tracking-[0.22em] uppercase border border-[#2a2a2d] text-[#9a9787]">Cancel</button>
          <button type="submit" className="px-6 py-3 text-[11px] tracking-[0.22em] uppercase bg-[#c3ba9e] text-[#0d0d0e]" data-testid="category-save">Save</button>
        </div>
      </form>
    </div>
  );
}

const input = "w-full bg-[#0d0d0e] border border-[#2a2a2d] px-3 py-2.5 text-[14px] outline-none focus:border-[#c3ba9e] disabled:opacity-50";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e] block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
